import { Box, Button, InputAdornment, MenuItem, Popover, Select, TextField, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const SharePublicApprovalPopup = ({
  popoverElement,
  handleCloseMenu,
  url,
  urlKey,
  shareUntil,
  getShareApprovalPosts,
}) => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();

  // Determine initial selection based on existing shareUntil
  const getInitialShareTime = () => {
    if (!urlKey || !shareUntil) {
      return '8 HOUR'; // no link yet → default to 8 hours
    }

    const expirationDate = new Date(shareUntil);
    const now = new Date();
    const diffMs = expirationDate - now;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours <= 0) {
      return '8 HOUR'; // already expired → treat as new
    }

    // If the expiration is very far in the future, treat as FOREVER
    if (diffHours > 24 * 365) {
      return 'FOREVER';
    }

    // Rough check for 1 day
    if (diffHours >= 23 && diffHours <= 25) {
      return '1 DAY';
    }

    // Default to 8 hours for everything else (or if it's exactly 8 hours)
    return '8 HOUR';
  };

  const [selectShareTime, setSelectShareTime] = useState(getInitialShareTime());

  // Re-calculate when shareUntil changes (e.g. after removing/creating link)
  useEffect(() => {
    setSelectShareTime(getInitialShareTime());
  }, [urlKey, shareUntil]);

  const handleActionChange = (shareTime) => {
    setSelectShareTime(shareTime);
  };

  const shareApprovalPosts = () => {
    const formBody = {
      share_until: selectShareTime,
    };
    const postProps = {
      url: userModuleURL.shareApprovalPosts,
      body: formBody,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const failureCallback = (message) => {
    message && showToast(message, 'e');
  };

  const successCallback = (data, message) => {
    getShareApprovalPosts();
    message && showToast(message, 's');
  };

  const removeApprovalPosts = () => {
    const postProps = {
      url: userModuleURL.removeApprovalPosts,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    showToast(`${translate('approval.share_approval.url_copied')}!`, 's');
  };

  return (
    <Popover
      open={Boolean(popoverElement)}
      anchorEl={popoverElement}
      onClose={handleCloseMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{
        sx: {
          p: 1,
          m: 1,
          '& .MuiMenuItem-root': {
            px: 1,
            typography: 'body2',
            borderRadius: 0.75,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="span" sx={{ fontWeight: 700 }}>
          {translate('approval.share_approval.share_your_post_with_public_link')}!
        </Typography>
        <Typography variant="body2">{translate('approval.share_approval.anyone_can_approve')}</Typography>

        <Select
          value={selectShareTime}
          onChange={(event) => handleActionChange(event.target.value)}
          size="small"
          sx={{ mt: 1 }}
        >
          <MenuItem value="8 HOUR">8 {translate('approval.share_approval.hour')}</MenuItem>
          <MenuItem value="1 DAY">1 {translate('approval.share_approval.day')}</MenuItem>
          <MenuItem value="FOREVER">{translate('approval.share_approval.forever')}</MenuItem>
        </Select>

        <Button variant="contained" onClick={shareApprovalPosts} sx={{ width: '150px' }}>
          {translate('approval.share_approval.share_public_link')}
        </Button>

        {urlKey && (
          <>
            <TextField
              size="small"
              value={url}
              readOnly
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={copyToClipboard} variant="contained">
                      {translate('approval.share_approval.copy_url')}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" color="text.secondary">
              Expires: {CommonHelper.formatDateAndTime(shareUntil)}
            </Typography>

            <Button
              variant="contained"
              color="error"
              onClick={removeApprovalPosts}
              sx={{ width: '150px' }}
            >
              {translate('approval.share_approval.remove_link')}
            </Button>
          </>
        )}
      </Box>
    </Popover>
  );
};

export default SharePublicApprovalPopup;